import React from 'react';
import classNames from 'classnames';

// Types
import { SuitabilityAnalysisResultType } from '../../../../redux/modules/types/suitability-analysis';

// Utils
import { formatLocalizedString, formatMessage, formatNumber, formatPercentage, FormattedMessage, formatHTMLMessageWithLocale } from '../../../../utils/i18n';
import { PILL_COLORS } from '../../../../utils/colors';

// Components
import { ControlItem, Tabs } from '../../navigation';
import DataTable from '../../data/DataTable';
import { Tooltip } from '../../tooltips';
import { PillLabel } from '../../misc';
import ExtendedSuitabilityAnalysisPortfolio from '@/types/extended-suitability-analysis-portfolio';
import LocalizedString from '@/types/localized-string';

enum TabOptions {
  POSITIONS = 'Positions',
  FUND_VARIABILITY = 'FundVariability',
}

type Props = {
  portfolio: ExtendedSuitabilityAnalysisPortfolio;
};

type State = {
  selectedTab: TabOptions;
};

class DetailedPortfolio extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedTab: TabOptions.POSITIONS,
    };
  }

  generatePositionColumns = () => [
    {
      Header: formatMessage('ProductSuitability.PositionsTable.PositionName'),
      accessor: 'name',
      className: 'position-name',
      width: 375,
      Cell: ({ value }: { value: LocalizedString }) =>
        value ? (
          <span data-private data-dd-privacy="mask">
            {formatLocalizedString(value)}
          </span>
        ) : null,
    },
    {
      Header: formatMessage('ProductSuitability.PositionsTable.Allocation'),
      accessor: 'weight',
      className: 'allocation',
      width: 100,
      Cell: ({ value }: { value: number }) => (typeof value === 'number' ? <span>{formatPercentage(value)}</span> : null),
    },
    {
      Header: formatMessage('ProductSuitability.PositionsTable.RiskScore'),
      accessor: 'riskScore',
      className: 'risk-score',
      width: 125,
      Cell: ({ value }: { value: number }) => (value ? <span>{formatNumber(value)}</span> : null),
    },
    {
      Header: formatMessage('ProductSuitability.PositionsTable.EquityComponent'),
      accessor: 'equityComponent',
      className: 'equity-component',
      Cell: ({ value }: { value: number }) => (typeof value === 'number' ? <span>{formatPercentage(value * 100, 0)}</span> : null),
    },
  ];

  generateVariabilityColumns = () => [
    {
      Header: formatMessage('ProductSuitability.VariabilityTable.FundVariability'),
      accessor: 'variabilityCategory',
      className: 'fund-variability',
      width: 170,
      Cell: (row: { index: number; pageSize: number; value: string }) => {
        const cellClasses = classNames({
          total: row.index + 1 === row.pageSize,
        });
        const str = formatHTMLMessageWithLocale(`ProductSuitability.VariabilityTable.Variability.${row.value}`, undefined, undefined, row.value);
        return row.value ? (
          <span
            // eslint-disable-next-line react/no-danger
            // This doesn't need to be sanitized since it's coming from the JSON locale files
            dangerouslySetInnerHTML={{ __html: str }}
            className={cellClasses}
          />
        ) : null;
      },
    },
    {
      Header: formatMessage('ProductSuitability.VariabilityTable.AcceptableMin'),
      accessor: 'acceptableMin',
      className: 'acceptable-minimum',
      Cell: ({ value }: { value: number }) => (typeof value === 'number' ? <span>{formatPercentage(value * 100, 0)}</span> : null),
    },
    {
      Header: formatMessage('ProductSuitability.VariabilityTable.AcceptableMax'),
      accessor: 'acceptableMax',
      className: 'acceptable-maximum',
      Cell: ({ value }: { value: number }) => (typeof value === 'number' ? <span>{formatPercentage(value * 100, 0)}</span> : null),
    },
    {
      Header: formatMessage('ProductSuitability.VariabilityTable.ActualPercentage'),
      accessor: 'actualPercentage',
      className: 'acceptable-percentage',
      width: 140,
      Cell: ({ value }: { value: number }) => (typeof value === 'number' ? <span>{formatPercentage(value * 100, 0)}</span> : null),
    },
    {
      Header: formatMessage('ProductSuitability.VariabilityTable.VariabilityResult'),
      accessor: 'variabilityResult',
      className: 'variability-result',
      width: 175,
      Cell: ({ value }: { value: SuitabilityAnalysisResultType }) => {
        let pillColor: PILL_COLORS;
        switch (value) {
          case SuitabilityAnalysisResultType.ERROR_PRODUCT_NOT_FOUND:
            pillColor = PILL_COLORS.ORANGE;
            break;
          case SuitabilityAnalysisResultType.OK:
            pillColor = PILL_COLORS.GREEN;
            break;
          case SuitabilityAnalysisResultType.REVIEW_REQUIRED:
            pillColor = PILL_COLORS.RED;
            break;
          default:
            pillColor = PILL_COLORS.GREY;
            break;
        }

        const resultMessage = `
            ${formatMessage(`ProductSuitability.SuitabilityResults.${value}.Prefix`)}
            ${formatMessage(`ProductSuitability.SuitabilityResults.${value}.Title`)}
          `;

        return value ? (
          <Tooltip delayShow={500} message={resultMessage}>
            <span>
              <PillLabel color={pillColor} text={resultMessage} />
            </span>
          </Tooltip>
        ) : null;
      },
    },
  ];

  renderDataTable = () => {
    const { portfolio } = this.props;
    const { selectedTab } = this.state;

    switch (selectedTab) {
      case TabOptions.POSITIONS:
        return (
          <DataTable
            className="suitability-portfolio-positions"
            columns={this.generatePositionColumns()}
            data={portfolio.items}
            noDataText={formatMessage('ProductSuitability.PositionsTable.NoData')}
          />
        );
      case TabOptions.FUND_VARIABILITY:
        return (
          <DataTable
            className="suitability-portfolio-variability"
            columns={this.generateVariabilityColumns()}
            data={portfolio.variabilityMix || []}
            noDataText={formatMessage('ProductSuitability.VariabilityTable.NoData')}
          />
        );
      default:
        return null;
    }
  };

  render() {
    const { selectedTab } = this.state;

    return (
      <div className="detailed-portfolio">
        <div className="tabs-wrapper">
          <Tabs onChange={tab => this.setState({ selectedTab: tab })} selected={selectedTab}>
            <ControlItem value={TabOptions.POSITIONS}>
              <FormattedMessage id="ProductSuitability.DetailedPortfolio.Tabs.Positions" />
            </ControlItem>
            <ControlItem value={TabOptions.FUND_VARIABILITY}>
              <FormattedMessage id="ProductSuitability.DetailedPortfolio.Tabs.FundVariability" />
            </ControlItem>
          </Tabs>
        </div>
        {this.renderDataTable()}
      </div>
    );
  }
}

export default DetailedPortfolio;
